import { useLazyQuery, useQuery } from '@apollo/client';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MapBoundsQuery,
  MapItemQuery,
  MapItemsQuery,
  MapMarkersQuery
} from '../../../graphql/graphql';
import {
  mapBoundsQuery,
  mapItemQuery,
  mapItemsQuery,
  mapMarkersQuery
} from '../../../api/queries';
import {
  getShrunkBoundsFromMap,
  isPointInBackendBounds
} from '../../../utils/helpers/map';
import { Marker } from '../../ItemsMap/types';
import { ManatIcon } from '../NewSearchBar/PriceFilter/styled';
import { CurrencySymbol } from '../../ui/ItemCard/CurrencySymbol';
import { PriceValue, SearchResultMapContainer } from './styled';
import { prettyNumber } from '../../../utils/helpers';
import { ItemsList } from './ItemsList';
import { useAccumulatedMapMarkers } from '../../../hooks/useAccumulatedMapMarkers';
import { ItemsMapPlaceholder } from '../../ItemsMap/ItemsMapPlaceholder';
import { ItemsMap } from '../../ItemsMap';
import { useLazyMapRender } from '../../../hooks/useLazyMapRender';
import { BackToFiltersButton } from './BackToFiltersButton';

interface SearchResultsItemsMapProps {
  filter: {
    [key: string]: string | boolean;
  };
  onTotalCountChange: (count: number) => void;
}

const units = (
  <ManatIcon className="manat-icon">
    <CurrencySymbol currency="AZN" />
  </ManatIcon>
);

const getItemLabel = (
  price: number,
  isLeased: boolean,
  leasedSuffix: string | boolean
) => (
  <PriceValue>
    {prettyNumber(price)}
    {units}
    {isLeased && `/${leasedSuffix}`}
  </PriceValue>
);

export const SearchResultsItemsMap: React.FC<SearchResultsItemsMapProps> = memo(
  ({ filter, onTotalCountChange }) => {
    const { t } = useTranslation();

    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [zoom, setZoom] = useState<number | undefined>(undefined);
    const { mapBlockRef, isMapVisible } = useLazyMapRender();

    const [activeSelection, setActiveSelection] = useState<Marker | null>(null);
    const [hasLoadedTotal, setHasLoadedTotal] = useState(false);

    const { data: mapBoundsData } = useQuery<MapBoundsQuery>(mapBoundsQuery, {
      variables: { filter }
    });

    const [mapBounds, setMapBounds] = useState<ReturnType<
      typeof getShrunkBoundsFromMap
    > | null>(null);

    const isInitialLoad = !hasLoadedTotal;

    const markersVariables = useMemo(
      () => ({
        filter: {
          ...filter,
          bounds: mapBounds
        },
        zoom,
        withTotal: isInitialLoad
      }),
      [filter, mapBounds, zoom]
    );

    const handleMarkersCompleted = useCallback(
      (data: MapMarkersQuery) => {
        const totalCount = data?.mapMarkers?.totalCount;

        if (isInitialLoad && totalCount !== undefined) {
          onTotalCountChange(totalCount);
          setHasLoadedTotal(true);
        }
      },
      [isInitialLoad, onTotalCountChange]
    );

    const { data: mapMarkersData, loading: markersLoading } =
      useQuery<MapMarkersQuery>(mapMarkersQuery, {
        skip: !map || zoom === undefined,
        variables: markersVariables,
        ssr: false,
        notifyOnNetworkStatusChange: true,
        onCompleted: handleMarkersCompleted
      });

    useEffect(() => {
      setHasLoadedTotal(false);
      setActiveSelection(null);
    }, [filter]);

    const clusterItemsBaseVariables = useMemo(
      () => ({
        first: 4,
        filter: {
          ...filter,
          bounds: mapBounds,
          mapClusterId: activeSelection?.id
        }
      }),
      [filter, activeSelection]
    );

    const {
      data: clusterItemsData,
      loading,
      fetchMore
    } = useQuery<MapItemsQuery>(mapItemsQuery, {
      variables: clusterItemsBaseVariables,
      skip:
        !activeSelection || activeSelection.type !== 'ClusterMapMarker' || !map,
      notifyOnNetworkStatusChange: true
    });

    const [
      loadSingleItem,
      { data: singleItemData, loading: singleItemLoading }
    ] = useLazyQuery<MapItemQuery>(mapItemQuery);

    useEffect(() => {
      if (!activeSelection || !map) return;

      const isCluster = activeSelection.type === 'ClusterMapMarker';

      if (!isCluster) {
        loadSingleItem({
          variables: {
            id: activeSelection.id
          }
        });
      }
    }, [activeSelection, map, loadSingleItem]);

    const clusterItems = useMemo(
      () => clusterItemsData?.mapItems?.edges?.map(e => e?.node) ?? [],
      [clusterItemsData]
    );

    const singleItem = useMemo(
      () => (singleItemData?.item ? [singleItemData.item] : []),
      [singleItemData]
    );

    const items = useMemo(() => {
      if (!activeSelection) return [];
      return activeSelection.type === 'ClusterMapMarker'
        ? clusterItems
        : singleItem;
    }, [activeSelection, clusterItems, singleItem]);

    const preparedMarkers = useMemo(() => {
      const markers = mapMarkersData?.mapMarkers.markers;
      const backendBounds = mapMarkersData?.mapMarkers.bounds;

      if (!markers || !backendBounds) return [];

      return markers
        .filter(marker =>
          isPointInBackendBounds(marker.lat, marker.lng, backendBounds)
        )
        .map(marker => {
          const isClusterMarker = marker.__typename === 'ClusterMapMarker';

          const leasedSuffix =
            !isClusterMarker &&
            t(
              marker.isPaidDaily
                ? 'new_search.leased_daily'
                : 'new_search.leased_monthly'
            );

          return {
            id: isClusterMarker ? marker.id : marker.itemId,

            type: marker.__typename,
            position: { lat: marker.lat, lng: marker.lng },
            label: isClusterMarker
              ? marker.count
              : getItemLabel(marker.price, marker.isLeased, leasedSuffix)
          };
        });
    }, [mapMarkersData, t]);

    const accumulatedMarkers = useAccumulatedMapMarkers(
      preparedMarkers,
      zoom,
      filter,
      markersLoading
    );

    const handleLoadMore = () => {
      const pageInfo = clusterItemsData?.mapItems?.pageInfo;
      if (!pageInfo?.hasNextPage || loading) return;

      fetchMore({
        variables: {
          ...clusterItemsBaseVariables,
          cursor: pageInfo.endCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newItems = fetchMoreResult?.mapItems;

          if (!newItems) {
            return previousResult;
          }

          return {
            ...previousResult,
            mapItems: {
              ...previousResult.mapItems,
              pageInfo: newItems.pageInfo,
              edges: [
                ...(previousResult.mapItems?.edges ?? []),
                ...(newItems.edges ?? [])
              ]
            }
          };
        }
      });
    };

    return (
      <SearchResultMapContainer
        $markersLoading={markersLoading}
        id="items-map"
        ref={mapBlockRef}
      >
        {isMapVisible ? (
          <>
            <ItemsMap
              title={t('search.ads')}
              zoom={zoom}
              map={map}
              mapBounds={mapBoundsData?.mapBounds}
              mapMarkers={accumulatedMarkers}
              activeSelection={activeSelection}
              setMap={setMap}
              setZoom={setZoom}
              setActiveSelection={setActiveSelection}
              onBoundsChange={setMapBounds}
            >
              {activeSelection && (
                <ItemsList
                  key={activeSelection.id}
                  items={items}
                  loading={loading || singleItemLoading}
                  hasNextPage={
                    clusterItemsData?.mapItems?.pageInfo?.hasNextPage
                  }
                  onLoadMore={handleLoadMore}
                  isClusterMarker={activeSelection.type === 'ClusterMapMarker'}
                />
              )}
            </ItemsMap>
            <BackToFiltersButton mapBlockRef={mapBlockRef} />
          </>
        ) : (
          <ItemsMapPlaceholder />
        )}
      </SearchResultMapContainer>
    );
  }
);

SearchResultsItemsMap.displayName = 'SearchResultsItemsMap';
